import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ChevronDown, Menu, Search, X } from 'lucide-react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { wikiApi, type WikiArticleCard, type WikiSection } from '@/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type WikiNavigation = {
  articles: WikiArticleCard[];
  sections: WikiSection[];
  search: string;
  filtered: WikiArticleCard[];
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
};
const WikiContext = createContext<WikiNavigation | null>(null);
export function useWikiNavigation() {
  const value = useContext(WikiContext);
  if (!value) throw new Error('WikiLayout is required');
  return value;
}

export function groupWikiArticles(articles: WikiArticleCard[], sections: WikiSection[], ungroupedTitle = sections.length ? 'Без раздела' : 'Основное') {
  const groups = sections.map((section) => ({ ...section, articles: articles.filter((article) => article.sectionId === section.id) }));
  const sectionIds = new Set(sections.map((section) => section.id));
  const ungrouped = articles.filter((article) => article.sectionId == null || !sectionIds.has(article.sectionId));
  return [...groups, { id: 0, title: ungroupedTitle, sortOrder: 0, articles: ungrouped }].filter((group) => group.articles.length > 0);
}

export function WikiLayout() {
  const articles = useQuery({ queryKey: ['wiki', 'list'], queryFn: wikiApi.list, staleTime: 60_000 });
  const sections = useQuery({ queryKey: ['wiki', 'sections'], queryFn: wikiApi.sections, staleTime: 60_000 });
  const [search, setSearch] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setMobileOpen(true);
        requestAnimationFrame(() => searchRef.current?.focus());
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);
  const query = search.trim().toLocaleLowerCase('ru');
  const filtered = (articles.data ?? []).filter((article) => {
    const section = sections.data?.find((entry) => entry.id === article.sectionId);
    return `${article.title} ${article.summary} ${section?.title ?? ''}`.toLocaleLowerCase('ru').includes(query);
  });
  const groups = groupWikiArticles(filtered, sections.data ?? []);
  const value: WikiNavigation = {
    articles: articles.data ?? [], sections: sections.data ?? [], search, filtered,
    isLoading: articles.isPending || sections.isPending,
    isError: articles.isError || sections.isError,
    retry: () => { void articles.refetch(); void sections.refetch(); },
  };
  return <WikiContext.Provider value={value}>
    <div className="mx-auto max-w-[1440px] px-5 py-8 md:px-8 lg:px-12 lg:py-12">
      <Button variant="secondary" className="mb-5 w-full justify-between lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} aria-expanded={mobileOpen} aria-controls="wiki-navigation">
        <span className="flex items-center gap-2"><Menu className="h-4 w-4" /> Разделы вики</span><ChevronDown className={cn('h-4 w-4 transition-transform', mobileOpen && 'rotate-180')} />
      </Button>
      <div className="grid items-start gap-8 lg:grid-cols-[220px_minmax(0,1fr)] xl:gap-12">
        <aside id="wiki-navigation" className={cn('min-w-0 rounded-xl border border-white/10 bg-eol-black/30 p-4 lg:sticky lg:top-24 lg:max-h-[calc(100dvh-120px)] lg:overflow-y-auto lg:rounded-none lg:border-0 lg:border-r lg:bg-transparent lg:py-0 lg:pl-0 lg:pr-6', !mobileOpen && 'hidden lg:block')}>
          <Link to="/wiki" className="mb-5 flex items-center gap-3 text-lg font-extrabold text-eol-white"><span className="rounded-lg bg-eol-pink/10 p-2"><BookOpen className="h-5 w-5 text-eol-pink" /></span>Вики EOL</Link>
          <div className="relative mb-6">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-eol-desc" />
            <input ref={searchRef} aria-label="Поиск по вики" placeholder="Найти статью…" className="h-10 w-full rounded-lg border border-white/10 bg-eol-black/50 pl-9 pr-8 text-sm outline-none transition focus:border-eol-pink/50 focus:ring-2 focus:ring-eol-pink/10" value={search} onChange={(event) => setSearch(event.target.value)} />
            {search && <button type="button" aria-label="Очистить поиск" className="absolute right-2 top-2 p-1 text-eol-desc hover:text-eol-white" onClick={() => { setSearch(''); searchRef.current?.focus(); }}><X className="h-4 w-4" /></button>}
          </div>
          <nav aria-label="Разделы вики" className="space-y-6">
            <NavLink to="/wiki" end className={({ isActive }) => cn('wiki-nav-link', isActive && 'wiki-nav-link-active')}><BookOpen className="h-4 w-4 shrink-0" />Все статьи</NavLink>
            {value.isLoading ? <p className="text-sm text-eol-desc" role="status">Загрузка разделов…</p> : value.isError ? <button className="text-sm text-eol-pink hover:underline" onClick={value.retry}>Повторить загрузку</button> : groups.map((group) => <details key={group.id} open className="group">
              <summary className="mb-2 flex cursor-pointer list-none items-center justify-between text-[11px] font-bold uppercase tracking-widest text-eol-desc">{group.title}<ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" /></summary>
              <div className="space-y-1">{group.articles.map((article) => <NavLink key={article.id} to={`/wiki/${article.slug}`} className={({ isActive }) => cn('wiki-nav-link', isActive && 'wiki-nav-link-active')}><span className="break-words">{article.title}</span></NavLink>)}</div>
            </details>)}
            {!value.isLoading && !value.isError && query && !filtered.length && <p className="text-sm leading-6 text-eol-desc">Статьи не найдены. Попробуйте другое название.</p>}
          </nav>
        </aside>
        <div className="min-w-0"><Outlet /></div>
      </div>
    </div>
  </WikiContext.Provider>;
}
